import { type FC, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import { cn } from '@/shared/libs/utils';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';

export type ConfirmDialogTone = 'danger' | 'default' | 'success';

export interface ConfirmDialogProps {
  busy?: boolean;
  cancelLabel: string;
  children?: ReactNode;
  confirmLabel: string;
  description?: string;
  onCancel: () => void;
  onConfirm: () => void;
  open: boolean;
  title: string;
  tone?: ConfirmDialogTone;
}

const CONFIRM_TONE_CLASS: Record<ConfirmDialogTone, string> = {
  danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  default:
    'brand-gradient text-brand-foreground shadow-lg shadow-brand/20 hover:opacity-95',
  success: 'bg-emerald-500 text-white hover:bg-emerald-600',
};

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  busy = false,
  cancelLabel,
  children,
  confirmLabel,
  description,
  onCancel,
  onConfirm,
  open,
  title,
  tone = 'danger',
}) => (
  <AlertDialog
    open={open}
    onOpenChange={(nextOpen) => {
      if (!nextOpen && !busy) {
        onCancel();
      }
    }}>
    <AlertDialogContent className="max-w-md">
      <AlertDialogHeader>
        <div className="flex items-start gap-3">
          {tone === 'danger' && (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-destructive/10">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
          )}
          <div className="min-w-0 space-y-2 text-left">
            <AlertDialogTitle>{title}</AlertDialogTitle>
            {!!description && (
              <AlertDialogDescription>{description}</AlertDialogDescription>
            )}
          </div>
        </div>
      </AlertDialogHeader>
      {children}
      <AlertDialogFooter className="gap-2 sm:gap-2">
        <AlertDialogCancel disabled={busy} onClick={onCancel}>
          {cancelLabel}
        </AlertDialogCancel>
        <AlertDialogAction
          className={cn('flex-1 sm:flex-none', CONFIRM_TONE_CLASS[tone])}
          disabled={busy}
          onClick={(event) => {
            event.preventDefault();
            onConfirm();
          }}>
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

ConfirmDialog.displayName = 'ConfirmDialog';
